import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { fetchEmailTemplates, sendEmail } from "@/api/email";
import { fetchLeads } from "@/api/crm";
import type { EmailTemplate } from "@/types/email";
import type { CrmLead } from "@/types/crm";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ToastProvider";

type ComposerMode = "new" | "reply" | "forward";

export type EmailComposerInitialValues = {
  to?: string;
  subject?: string;
  body_html?: string;
};

type EmailComposerDrawerProps = {
  open: boolean;
  mode: ComposerMode;
  initial: EmailComposerInitialValues;
  contactId?: number | null;
  leadId?: number | null;
  refreshKey?: number;
  onClose: () => void;
  onSent?: () => void;
  onOpenTemplateManager?: () => void;
};

export function EmailComposerDrawer({
  open,
  mode,
  initial,
  contactId,
  leadId,
  refreshKey,
  onClose,
  onSent,
  onOpenTemplateManager,
}: EmailComposerDrawerProps) {
  const { push } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [toValue, setToValue] = useState(initial.to ?? "");
  const [subjectValue, setSubjectValue] = useState(initial.subject ?? "");
  const [bodyValue, setBodyValue] = useState(initial.body_html ?? "");
  const [sending, setSending] = useState(false);
  const [availableLeads, setAvailableLeads] = useState<CrmLead[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(leadId ?? null);

  useEffect(() => {
    if (!open) return;
    setToValue(initial.to ?? "");
    setSubjectValue(initial.subject ?? "");
    setBodyValue(initial.body_html ?? "");
    setSelectedLeadId(leadId ?? null);
    setSelectedTemplateId(null);
  }, [open, initial.to, initial.subject, initial.body_html, leadId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTemplatesLoading(true);
    fetchEmailTemplates()
      .then((items) => {
        if (!cancelled) {
          setTemplates(items ?? []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Unable to load email templates.";
          push({ title: "Email templates", message });
        }
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, push, refreshKey]);

  useEffect(() => {
    if (!open) return;
    if (typeof document === "undefined") return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setSubjectValue((prev) => prev || selectedTemplate.subject || "");
    setBodyValue((prev) => prev || selectedTemplate.body_html || "");
  }, [selectedTemplate]);

  const readyToSend = toValue.trim().length > 3 && Boolean(subjectValue.trim() && bodyValue.trim());

  useEffect(() => {
    if (!open || !contactId) return;
    let cancelled = false;
    (async () => {
      try {
        const leads = await fetchLeads({ status: "open" });
        if (cancelled) return;
        const matching = leads.filter((lead) => lead.contact_id === contactId);
        setAvailableLeads(matching);
        if (!selectedLeadId) {
          if (matching.length === 1) {
            setSelectedLeadId(matching[0].id);
          }
        }
      } catch {
        if (!cancelled) {
          setAvailableLeads([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, contactId, selectedLeadId]);

  if (!open || typeof document === "undefined") return null;

  const handleSend = async () => {
    if (!readyToSend) {
      push({ title: "Missing fields", message: "Add a recipient, subject, and body before sending." });
      return;
    }
    setSending(true);
    try {
      await sendEmail({
        to: toValue.trim(),
        subject: subjectValue.trim(),
        body_html: bodyValue,
        contact_id: contactId ?? undefined,
        lead_id: selectedLeadId ?? undefined,
      });
      push({ title: "Email sent", message: `Message queued to ${toValue.trim()}` });
      onSent?.();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to send email.";
      push({ title: "Send failed", message });
    } finally {
      setSending(false);
    }
  };

  const personalizationTokens = [
    "{{contact_name}}",
    "{{venue_name}}",
    "{{city}}",
    "{{event_window}}",
    "{{artist_name}}",
  ];
  const artistAssetTokens = [
    "{{artist_logo_url}}",
    "{{artist_band_photo_url}}",
    "{{artist_video_link_1}}",
    "{{artist_video_link_2}}",
    "{{artist_video_link_3}}",
    "{{artist_audio_link_1}}",
    "{{artist_audio_link_2}}",
    "{{artist_audio_link_3}}",
  ];
  const artistSocialTokens = [
    "{{artist_website}}",
    "{{artist_instagram}}",
    "{{artist_facebook}}",
    "{{artist_x_url}}",
    "{{artist_youtube_url}}",
    "{{artist_tiktok}}",
    "{{artist_spotify_url}}",
    "{{artist_apple_music_url}}",
    "{{artist_bandcamp_url}}",
    "{{artist_bandsintown_url}}",
  ];

  const modeLabel = mode === "reply" ? "Reply" : mode === "forward" ? "Forward" : "Compose";

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-2 py-3 sm:px-4">
      <div className="flex h-[min(92vh,56rem)] w-full max-w-[min(96vw,76rem)] flex-col overflow-hidden rounded-3xl border border-border bg-background shadow-2xl">
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Manual Email</p>
            <h2 className="text-lg font-semibold text-foreground">{modeLabel}</h2>
          </div>
          <button
            type="button"
            className="rounded-full border border-border p-1.5 text-foreground/70 transition-all duration-200 hover:border-primary/70 hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="space-y-3">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              To
              <input
                type="email"
                value={toValue}
                onChange={(event) => setToValue(event.target.value)}
                placeholder="recipient@example.com"
                className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              />
            </label>
            {availableLeads.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span className="uppercase tracking-wide">Associate with lead</span>
                <select
                  className="rounded-full border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground"
                  value={selectedLeadId ?? ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedLeadId(value ? Number(value) : null);
                  }}
                >
                  <option value="">None (link later)</option>
                  {availableLeads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.title || `Lead ${lead.id}`} · {lead.pipeline_stage}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
              <span className="rounded-full border border-border px-2 py-0.5">
                CC / BCC
                <span className="ml-1 text-muted-foreground">(stubbed – use To for now)</span>
              </span>
            </div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Subject
              <input
                type="text"
                value={subjectValue}
                onChange={(event) => setSubjectValue(event.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              />
            </label>
          </div>
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Body</p>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span className="uppercase tracking-wide">Templates</span>
                <select
                  className="rounded-full border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground"
                  value={selectedTemplateId ?? ""}
                  onChange={(event) => {
                    const value = event.target.value || null;
                    setSelectedTemplateId(value);
                  }}
                  disabled={templatesLoading || !templates.length}
                >
                  <option value="">None</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                {onOpenTemplateManager && (
                  <button
                    type="button"
                    onClick={onOpenTemplateManager}
                    className="text-[11px] text-primary underline-offset-2 hover:underline"
                  >
                    Manage →
                  </button>
                )}
              </div>
            </div>
            <RichTextEditor
              value={bodyValue}
              onChange={setBodyValue}
              placeholder="Write your message…"
              minHeight={220}
            />
          </section>
          <section className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Personalization tokens</p>
            <div className="flex flex-wrap gap-2">
              {personalizationTokens.map((token) => (
                <button
                  key={token}
                  type="button"
                  onClick={() => setBodyValue((prev) => `${prev || ""} ${token}`.trim())}
                  className="rounded-full border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground transition-all duration-200 hover:border-primary/70 hover:bg-primary/10"
                >
                  {token}
                </button>
              ))}
            </div>
          </section>
          <section className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Artist assets</p>
            <div className="flex flex-wrap gap-2">
              {artistAssetTokens.map((token) => (
                <button
                  key={token}
                  type="button"
                  onClick={() => setBodyValue((prev) => `${prev || ""} ${token}`.trim())}
                  className="rounded-full border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground transition-all duration-200 hover:border-primary/70 hover:bg-primary/10"
                >
                  {token}
                </button>
              ))}
            </div>
          </section>
          <section className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Artist socials</p>
            <div className="flex flex-wrap gap-2">
              {artistSocialTokens.map((token) => (
                <button
                  key={token}
                  type="button"
                  onClick={() => setBodyValue((prev) => `${prev || ""} ${token}`.trim())}
                  className="rounded-full border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground transition-all duration-200 hover:border-primary/70 hover:bg-primary/10"
                >
                  {token}
                </button>
              ))}
            </div>
          </section>
        </div>
        <footer className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 sm:px-6">
          <p className="text-[11px] text-muted-foreground">
            Manual-only composer · no AI draft or approvals required.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              type="button"
              onClick={() => void handleSend()}
              disabled={!readyToSend || sending}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
